import { useEffect, useId, useRef, useState } from 'react';
import { LenderCreditCompleteness, LenderCompletenessV2 } from '../../services/lenderApiService';

// CC-024: Credit Completeness Badge for Lender Dashboard
// Handles all states: Complete, Mostly Complete, Incomplete, No Credit, No Data
// CC-024B: No-Credit rendering included (zero known accounts)
// CC-210: v2-aware — consumes completeness_v2 (overall_score, profile_score,
//         reasons, credit_mix) when present; falls back to v1 for older payloads.
//         Fixes the "No Credit / 0%" false-zero for installment-only members.

const FRESHNESS_WINDOW_DAYS = 30;

interface BadgeConfig {
  dot: string;    // Tailwind dot color
  bg: string;     // Tailwind background
  text: string;   // Tailwind text color
  label: string;  // Display label
}

/**
 * CC-210: Returns true when v2 payload indicates the member has a real credit
 * profile (installment-only members pass this check even when known_accounts=0).
 */
function hasV2Profile(v2: LenderCompletenessV2 | null | undefined): boolean {
  return !!v2 && typeof v2.profile_score === 'number' && v2.profile_score > 0;
}

function deriveBadge(data: LenderCreditCompleteness | null | undefined): BadgeConfig {
  if (!data) {
    return { dot: 'bg-slate-300', bg: 'bg-slate-100', text: 'text-slate-500', label: 'No Data' };
  }

  const v2 = data.completeness_v2;
  const isStale = data.freshness_oldest_days !== null && data.freshness_oldest_days > FRESHNESS_WINDOW_DAYS;

  // CC-210: v2-aware path — take precedence when a v2 payload is attached.
  if (v2) {
    // False-zero fix: known_accounts=0 but profile_score>0 means the member has
    // a valid credit file (e.g., installment-only) — surface it as "Bureau Verified",
    // not "No Credit".
    if (data.known_accounts === 0 && !hasV2Profile(v2)) {
      return { dot: 'bg-slate-400', bg: 'bg-slate-50', text: 'text-slate-600', label: 'No Credit' };
    }
    const overall = Math.round(v2.overall_score);
    if (overall >= 80 && !isStale) {
      return { dot: 'bg-green-500', bg: 'bg-green-50', text: 'text-green-700', label: 'Complete' };
    }
    if (overall >= 50 && !isStale) {
      return { dot: 'bg-yellow-500', bg: 'bg-yellow-50', text: 'text-yellow-700', label: 'Mostly Complete' };
    }
    // Installment-only thin-file members land here (e.g., overall=37 for Anne) —
    // rendered as "Bureau Verified" instead of "Incomplete" to reflect that the
    // data IS complete for what's connectable; the profile is simply thin.
    if (data.known_accounts === 0 && hasV2Profile(v2)) {
      return { dot: 'bg-blue-500', bg: 'bg-blue-50', text: 'text-blue-700', label: 'Bureau Verified' };
    }
    return { dot: 'bg-red-500', bg: 'bg-red-50', text: 'text-red-700', label: 'Incomplete' };
  }

  // Legacy v1-only path (preserved for pre-framework/backfill-pending members).
  if (data.known_accounts === 0) {
    return { dot: 'bg-slate-400', bg: 'bg-slate-50', text: 'text-slate-600', label: 'No Credit' };
  }
  if (data.is_complete && !isStale) {
    return { dot: 'bg-green-500', bg: 'bg-green-50', text: 'text-green-700', label: 'Complete' };
  }
  if (data.completeness_pct >= 80 && !isStale) {
    return { dot: 'bg-yellow-500', bg: 'bg-yellow-50', text: 'text-yellow-700', label: 'Mostly Complete' };
  }
  return { dot: 'bg-red-500', bg: 'bg-red-50', text: 'text-red-700', label: 'Incomplete' };
}

function buildTooltip(data: LenderCreditCompleteness | null | undefined): string {
  if (!data) return 'Credit completeness data not available';
  const v2 = data.completeness_v2;
  const staleNote = data.freshness_oldest_days !== null && data.freshness_oldest_days > FRESHNESS_WINDOW_DAYS
    ? ` · Data is ${data.freshness_oldest_days} days old`
    : '';

  // CC-210: v2-aware tooltip emphasizes profile vs coverage.
  if (v2) {
    if (data.known_accounts === 0 && !hasV2Profile(v2)) return 'No credit accounts on file';
    const profile = Math.round(v2.profile_score);
    const overall = Math.round(v2.overall_score);
    const coverageNote = v2.coverage_score !== null
      ? ` · Coverage ${Math.round(v2.coverage_score)}%`
      : ' · No revolving accounts to connect';
    return `Profile ${profile}% · Overall ${overall}%${coverageNote}${staleNote}`;
  }

  // v1 fallback
  if (data.known_accounts === 0) return 'No credit accounts on file';
  const verified = data.verified_live_accounts;
  const total = data.known_accounts;
  return `${verified} of ${total} credit accounts verified via live connection${staleNote}`;
}

// CC-210: Severity-to-style mapping for reason codes (mirrors CC-209 member UI).
function reasonSeverityStyle(severity: string): { bg: string; text: string; icon: string } {
  switch (severity) {
    case 'action':
      return { bg: 'bg-red-50', text: 'text-red-700', icon: '⚠' };
    case 'warning':
      return { bg: 'bg-amber-50', text: 'text-amber-700', icon: '⚠' };
    case 'info':
    default:
      return { bg: 'bg-slate-50', text: 'text-slate-600', icon: 'ℹ' };
  }
}

// CC-210: Primary numeric shown in the pill — prefers v2.overall_score, falls
// back to v1.completeness_pct. Returns null to suppress the number (no data).
function pillPercent(data: LenderCreditCompleteness): number | null {
  const v2 = data.completeness_v2;
  if (v2) return Math.round(v2.overall_score);
  if (data.known_accounts > 0) return Math.round(data.completeness_pct);
  return null;
}

interface CreditCompletenessBadgeProps {
  data: LenderCreditCompleteness | null | undefined;
  /** When true, shows expandable unresolved account details on click (for 360 view) */
  expandable?: boolean;
}

export default function CreditCompletenessBadge({ data, expandable = false }: CreditCompletenessBadgeProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  // BUG-CC-LUI-001: Controlled tooltip state replaces native HTML title attribute,
  // which browsers auto-dismiss after ~2 s regardless of cursor position.
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const tooltipId = useId();

  const badge = deriveBadge(data);
  const tooltip = buildTooltip(data);
  const v2 = data?.completeness_v2 ?? null;
  const creditMix = v2?.credit_mix ?? null;
  const reasons = v2?.reasons ?? [];
  const hasUnresolved = !!data && data.unresolved_accounts > 0;
  // CC-210: Dropdown has meaningful content when v2 data is present OR there
  // are unresolved accounts to enumerate. List-view (non-expandable) stays compact.
  const hasDropdownContent = hasUnresolved || !!creditMix || reasons.length > 0;
  const canExpand = expandable && !!data && hasDropdownContent;
  const percent = data ? pillPercent(data) : null;
  // CC-210: Installment-only "Verified via Bureau" callout — shown when v2
  // confirms a real profile without any revolving accounts to connect.
  const showBureauOnly = !!v2 && !!creditMix &&
    creditMix.revolving === 0 && creditMix.installment > 0;

  // BUG-CC-LUI-001: 100 ms grace bridge keeps the tooltip alive while the cursor
  // travels between the trigger and the tooltip content.
  const openTooltip = () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
    setTooltipOpen(true);
  };
  const scheduleCloseTooltip = () => {
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    closeTimerRef.current = setTimeout(() => {
      setTooltipOpen(false);
      closeTimerRef.current = null;
    }, 100);
  };

  // Clear any pending close timer on unmount to avoid setState-after-unmount.
  useEffect(() => {
    return () => {
      if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    };
  }, []);

  // BUG-CC-LUI-001: Click-outside and Escape dismiss both tooltip and dropdown.
  // Listener is only attached while at least one of them is open.
  useEffect(() => {
    if (!tooltipOpen && !isExpanded) return;
    const handleMouseDown = (e: MouseEvent) => {
      if (wrapperRef.current && wrapperRef.current.contains(e.target as Node)) return;
      setTooltipOpen(false);
      setIsExpanded(false);
    };
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      setTooltipOpen(false);
      setIsExpanded(false);
    };
    document.addEventListener('mousedown', handleMouseDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [tooltipOpen, isExpanded]);

  // BUG-CC-LUI-002/003: When expandable, the wrapper takes the parent's full
  // width so the in-flow dropdown reads as an integrated card section, not a
  // floating overlay. Non-expandable (list-row) usage keeps inline-flex sizing
  // so the pill continues to sit inline next to the member name.
  const wrapperClass = expandable ? 'flex flex-col w-full' : 'inline-flex flex-col';

  return (
    <div ref={wrapperRef} className={wrapperClass}>
      {/* Pill + tooltip — relative wrapper anchors the tooltip to the pill,
          not to the dropdown. */}
      <div className="relative inline-flex">
        <button
          type="button"
          className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${badge.bg} ${badge.text} ${canExpand ? 'cursor-pointer hover:ring-1 hover:ring-current/20' : 'cursor-default'}`}
          onClick={() => {
            if (!canExpand) return;
            setIsExpanded((prev) => !prev);
            // Close tooltip when opening/closing dropdown so they don't overlap.
            setTooltipOpen(false);
          }}
          onMouseEnter={openTooltip}
          onMouseLeave={scheduleCloseTooltip}
          onFocus={openTooltip}
          onBlur={scheduleCloseTooltip}
          aria-expanded={canExpand ? isExpanded : undefined}
          aria-describedby={tooltipOpen ? tooltipId : undefined}
          aria-label={`Credit completeness: ${badge.label}`}
        >
          <span className={`w-2 h-2 rounded-full ${badge.dot}`} aria-hidden="true" />
          {badge.label}
          {percent !== null && (
            <span className="opacity-70">
              {percent}%
            </span>
          )}
          {canExpand && (
            <svg
              className={`w-3 h-3 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
              fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
              aria-hidden="true"
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
            </svg>
          )}
        </button>

        {/* BUG-CC-LUI-001: Controlled tooltip — persists while cursor is over
            trigger or content; dismisses on mouse-leave (with 100 ms grace
            bridge), Escape, or click-outside. */}
        {tooltipOpen && (
          <div
            id={tooltipId}
            role="tooltip"
            onMouseEnter={openTooltip}
            onMouseLeave={scheduleCloseTooltip}
            className="absolute z-20 top-full left-0 mt-1 px-2 py-1.5 rounded bg-slate-800 text-white text-xs leading-snug shadow-lg max-w-xs"
          >
            {tooltip}
          </div>
        )}
      </div>

      {/* CC-210: Expandable dropdown — credit mix, reasons, bureau callout,
          and legacy unresolved-account list. Only shown in expandable mode.
          BUG-CC-LUI-002: max-w-full + overflow-hidden clamp to parent card width. */}
      {canExpand && isExpanded && data && (
        <div className="mt-2 ml-1 pl-3 border-l-2 border-slate-200 space-y-3 max-w-full overflow-hidden">
          {/* CC-210: V2 score breakdown — Profile + Coverage */}
          {v2 && (
            <div className="text-xs text-slate-600 space-y-0.5">
              <div className="font-medium text-slate-700">Score Breakdown</div>
              <div className="flex flex-wrap gap-x-3 gap-y-1 text-slate-500">
                <span>Profile: <span className="font-semibold text-slate-700">{Math.round(v2.profile_score)}%</span></span>
                {v2.coverage_score !== null && (
                  <span>Coverage: <span className="font-semibold text-slate-700">{Math.round(v2.coverage_score)}%</span></span>
                )}
                <span>Overall: <span className="font-semibold text-slate-700">{Math.round(v2.overall_score)}%</span></span>
              </div>
            </div>
          )}

          {/* CC-210: Credit mix breakdown (revolving / installment / other) */}
          {creditMix && creditMix.total > 0 && (
            <div className="text-xs text-slate-600 space-y-0.5">
              <div className="font-medium text-slate-700">Credit Mix</div>
              <div className="flex flex-wrap gap-x-3 gap-y-1 text-slate-500">
                <span>{creditMix.revolving} revolving</span>
                <span aria-hidden="true">·</span>
                <span>{creditMix.installment} installment</span>
                <span aria-hidden="true">·</span>
                <span>{creditMix.other} other</span>
              </div>
            </div>
          )}

          {/* CC-210: Installment-only "Verified via Bureau" callout */}
          {showBureauOnly && (
            <div className="text-xs rounded px-2 py-1.5 bg-blue-50 text-blue-700 border border-blue-100">
              <span className="font-medium">Verified via Bureau:</span>
              {' '}
              {creditMix!.installment} installment loan{creditMix!.installment !== 1 ? 's' : ''}
              {' '}confirmed from credit report. No revolving accounts to connect.
            </div>
          )}

          {/* CC-210: Reason codes (mirrors CC-209 member-side severity styling) */}
          {reasons.length > 0 && (
            <div className="space-y-1">
              <div className="text-xs font-medium text-slate-700">Why this score ({reasons.length})</div>
              <ul className="space-y-1">
                {reasons.map((r, idx) => {
                  const style = reasonSeverityStyle(r.severity);
                  return (
                    <li
                      key={`${r.code}-${idx}`}
                      className={`text-xs rounded px-2 py-1 flex items-start gap-1.5 ${style.bg} ${style.text}`}
                    >
                      <span aria-hidden="true" className="flex-shrink-0 font-semibold">{style.icon}</span>
                      <span>{r.text}</span>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}

          {/* Legacy unresolved-account list — preserved verbatim */}
          {hasUnresolved && (
            <div className="space-y-2">
              <p className="text-xs font-medium text-slate-700">
                {data.unresolved_accounts} unresolved account{data.unresolved_accounts !== 1 ? 's' : ''}
              </p>
              {data.unresolved_details.length > 0 ? (
                data.unresolved_details.map((acct, idx) => (
                  <div key={acct.account_ref || `${acct.creditor_name}-${acct.account_mask}-${idx}`} className="text-xs text-slate-600 space-y-0.5">
                    <div className="font-medium text-slate-700 flex items-center gap-2">
                      <span>{acct.creditor_name}</span>
                      {acct.account_mask && <span className="text-slate-400">{acct.account_mask}</span>}
                      {acct.severity && (
                        <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${
                          acct.severity === 'high' ? 'bg-red-100 text-red-700' :
                          acct.severity === 'medium' ? 'bg-amber-100 text-amber-700' :
                          'bg-slate-100 text-slate-600'
                        }`}>
                          {acct.severity}
                        </span>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-slate-500">
                      <span>{acct.account_type}</span>
                      {acct.bureau_balance !== null && acct.bureau_balance !== undefined && (
                        <span>Balance: {typeof acct.bureau_balance === 'string' ? acct.bureau_balance : `$${acct.bureau_balance.toLocaleString()}`}</span>
                      )}
                      <span className="text-red-500 font-medium">{acct.issue_type || acct.status}</span>
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-xs text-slate-400 italic">Account details unavailable</p>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
